import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
} from "@react-pdf/renderer";

// --- DYNAMIC PERMISSION LOGIC (No changes needed here) ---
const usePermissions = () => {
  const authData = useMemo(() => {
    try {
      const userDetailsString = localStorage.getItem("zTaskUserDetails");
      if (!userDetailsString) return { user: null, permissions: [] };
      const userDetails = JSON.parse(userDetailsString);
      const permissions = userDetails?.role?.permissions
        ? userDetails.role.permissions.map((p) => p.title)
        : [];
      return { user: userDetails, permissions };
    } catch (error) {
      console.error("Failed to parse user details from localStorage:", error);
      return { user: null, permissions: [] };
    }
  }, []);

  const hasPermission = useCallback(
    (requiredPermission) => {
      return authData.permissions.includes(requiredPermission);
    },
    [authData]
  );

  return { user: authData.user, hasPermission };
};
// --- END PERMISSION LOGIC ---

// --- STYLES (No changes needed here) ---
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: 30,
    color: "#495057",
  },
  viewer: { width: "100%", height: "90vh" },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#dee2e6",
    paddingBottom: 15,
  },
  companyInfo: { width: "56%" },
  companyName: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0d6efd",
    marginBottom: 5,
    textWrap: "balance",
  },
  companyDetails: { fontSize: 9, lineHeight: 1.4, width: "68%" },
  workOrderInfo: { width: "33%", textAlign: "right", flexShrink: 0 },
  workOrderTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#6c757d",
    marginBottom: 10,
  },
  woNumberSection: { marginBottom: 10 },
  woLabel: {
    backgroundColor: "#0d6efd",
    color: "white",
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "center",
  },
  woValue: {
    backgroundColor: "#e9ecef",
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    textAlign: "center",
    fontWeight: "bold",
    borderWidth: 1,
    borderColor: "#ced4da",
  },
  billToSection: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#ced4da",
    marginBottom: 15,
  },
  billToHeader: {
    backgroundColor: "#0d6efd",
    color: "white",
    padding: 8,
    fontWeight: "bold",
    textAlign: "center",
    width: "50%",
  },
  billToContentContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#ced4da",
    borderTopWidth: 0,
    marginBottom: 15,
  },
  billToContent: { padding: 10, width: "50%" },
  detailsTable: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#ced4da",
    marginBottom: 15,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0d6efd",
    color: "white",
    padding: 8,
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6",
    padding: 8,
  },
  tableCell: { textAlign: "center", padding: 5 },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  sectionGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  commentsSection: {
    width: "100%",
    backgroundColor: "#f1f3f5",
    padding: 10,
    borderRadius: 4,
  },
  commentsTitle: { fontWeight: "bold", marginBottom: 5 },
  fieldRow: { flexDirection: "row", marginBottom: 5 },
  fieldLabel: { fontWeight: "bold", width: 100 },
  fullWidthSection: { width: "100%", marginBottom: 20 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#212529",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0d6efd",
    paddingBottom: 5,
  },
  bold: { fontWeight: "bold" },
});

export const WorkOrderDocument = ({ allData, company }) => {
  if (!allData || !company) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text>Loading data...</Text>
        </Page>
      </Document>
    );
  }

  const { customer, project, work_order_tran } = allData;
  
  // FIXED: Filter out null/undefined transactions
  const validTransactions = (work_order_tran || []).filter(
    tran => tran !== null && tran !== undefined && typeof tran === 'object'
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerSection}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{company.companyName}</Text>
            <Text style={styles.companyDetails}>
              <Text style={styles.bold}>Address:</Text> {company.address}
              {"\n"}
              <Text style={styles.bold}>Phone:</Text> {company.telephoneNumber}
              {"\n"}
              <Text style={styles.bold}>Website:</Text> {company.websiteUrl}
            </Text>
          </View>
          <View style={styles.workOrderInfo}>
            <Text style={styles.workOrderTitle}>WORK ORDER</Text>
            <View style={styles.woNumberSection}>
              <Text style={styles.woLabel}>WORK ORDER #</Text>
              <Text style={styles.woValue}>{allData.wo_no || "N/A"}</Text>
            </View>
            <View>
              <Text style={styles.woLabel}>DATE</Text>
              <Text style={styles.woValue}>
                {allData.wo_date
                  ? new Date(allData.wo_date).toLocaleDateString()
                  : "N/A"}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.billToSection}>
          <Text style={styles.billToHeader}>CUSTOMER DETAILS</Text>
          <Text style={styles.billToHeader}>PROJECT DETAILS</Text>
        </View>
        <View style={styles.billToContentContainer}>
          <View style={styles.billToContent}>
            <Text>
              <Text style={styles.bold}>Name:</Text> {customer?.name || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>Address:</Text>{" "}
              {customer?.address || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>City:</Text> {customer?.city || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>Pincode:</Text>{" "}
              {customer?.pincode || "N/A"}
            </Text>
          </View>
          <View style={styles.billToContent}>
            <Text>
              <Text style={styles.bold}>Name:</Text>{" "}
              {project?.projectName || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>Address:</Text>{" "}
              {project?.address || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>City:</Text> {project?.city || "N/A"}
            </Text>
            <Text>
              <Text style={styles.bold}>Pincode:</Text>{" "}
              {project?.pinCode || "N/A"}
            </Text>
          </View>
        </View>

        <View style={styles.fullWidthSection}>
          <Text style={styles.sectionTitle}>Work Order Details</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>WO Start:</Text>
            <Text>
              {allData.wo_start
                ? new Date(allData.wo_start).toLocaleString()
                : "N/A"}
            </Text>
          </View>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>WO End:</Text>
            <Text>
              {allData.wo_end
                ? new Date(allData.wo_end).toLocaleString()
                : "N/A"}
            </Text>
          </View>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Remarks:</Text>
            <Text>{allData.remarks || "No remarks provided"}</Text>
          </View>
        </View>

        <View style={styles.detailsTable}>
          <View style={styles.tableHeader}>
            <Text style={{ width: "8%" }}>SR.</Text>
            <Text style={{ width: "42%", textAlign: "left" }}>ASSET</Text>
            <Text style={{ width: "15%" }}>START</Text>
            <Text style={{ width: "15%" }}>END</Text>
            <Text style={{ width: "20%", textAlign: "right" }}>RATE</Text>
          </View>
          {validTransactions.map((asset, index) => (
            <View style={styles.tableRow} key={asset.id || index}>
              <Text style={[styles.tableCell, { width: "8%" }]}>
                {asset.wo_sr_no}
              </Text>
              <Text
                style={[styles.tableCell, styles.textLeft, { width: "42%" }]}
              >
                {asset.asset_name}
              </Text>
              <Text style={[styles.tableCell, { width: "15%" }]}>
                {new Date(asset.start_date).toLocaleDateString()}
              </Text>
              <Text style={[styles.tableCell, { width: "15%" }]}>
                {new Date(asset.end_date).toLocaleDateString()}
              </Text>
              <Text
                style={[styles.tableCell, styles.textRight, { width: "20%" }]}
              >
                {asset.daily_rate}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

function WorkOrderPrint() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("zTaskAuthToken");
  const { user, hasPermission } = usePermissions();

  useEffect(() => {
    if (!hasPermission("DOWNLOAD WORK ORDER PDF")) {
      setError(
        "Access Denied: You do not have permission to view this document."
      );
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch all data sources in parallel for efficiency
        const [companyRes, woRes, customersRes, projectsRes] =
          await Promise.all([
            fetch(
              `${process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL}/api/get-company`,
              { headers }
            ),
            fetch(
              `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/${id}`,
              { headers }
            ),
            fetch(
              `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers`,
              { headers }
            ),
            fetch(
              `${process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL}/api/get-project`,
              { headers }
            ),
          ]);

        // Check all responses
        if (!companyRes.ok) throw new Error("Failed to fetch company data.");
        if (!woRes.ok) throw new Error("Failed to fetch work order data.");
        if (!customersRes.ok)
          throw new Error("Failed to fetch customers list.");
        if (!projectsRes.ok) throw new Error("Failed to fetch projects list.");

        const companyData = await companyRes.json();
        const workOrderDetails = await woRes.json();
        const allCustomers = await customersRes.json();
        const allProjectsData = await projectsRes.json();
        const allProjects = allProjectsData.projects || [];

        // Security check
        if (user?.id !== workOrderDetails.added_by) {
          throw new Error(
            "Access Denied: You can only download your own work orders."
          );
        }

        // Use loose equality (==) to find the matching customer and project
        const foundCustomer = allCustomers.find(
          (c) => c.id == workOrderDetails.cust_id
        );
        const foundProject = allProjects.find(
          (p) => p.projectId == workOrderDetails.project_id
        );

        const combinedData = {
          ...workOrderDetails,
          customer: foundCustomer,
          project: foundProject,
        };

        setCompany(companyData.message[0]);
        setAllData(combinedData);
      } catch (err) {
        console.error("Failed to fetch data for PDF:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, token, hasPermission, user]);

  if (isLoading) return <Text>Loading document...</Text>;
  if (error) return <Text>Error: {error}</Text>;
  if (!hasPermission("DOWNLOAD WORK ORDER PDF"))
    return (
      <Text>
        Access Denied. You do not have permission to view this document.
      </Text>
    );
  if (!allData || !company)
    return <Text>Could not load data for the document.</Text>;

  return (
    <PDFViewer style={styles.viewer}>
      <WorkOrderDocument allData={allData} company={company} />
    </PDFViewer>
  );
}

export default WorkOrderPrint;